import React from "react";
import styles from "../stylesERv2.module.scss";

/**
 * Detalle de "Costo intermediación" (Bloque A) del ER v2 para un mes: agencia + broker +
 * programática (take del exchange) + APX (Publicis). Solo lectura.
 *
 * Mismas columnas e información que el AgencyCommissionModal del v1 (Factura / Cliente /
 * entidad / Importe / % / valor), con el maquetado del ER v2 (modalOverlay + modalContent +
 * modalTable + clases agency*). NO usa los grupos colapsables del v1 (esas clases,
 * modalInterm y modalGrupo, no existen en el SCSS de v2): cada grupo con datos se muestra
 * como una tabla titulada y el total combinado va al pie. Los arrays llegan YA combinados
 * (pauta + contratos) y filtrados por mes desde index.js; acá no hay lógica de datos.
 */
export const DetalleIntermediacionModal = ({
  isOpen,
  mes,
  comisiones = [],
  comisionesBroker = [],
  costosProgramaticos = [],
  apx = [],
  oportunidades = [],
  formatCurrency,
  pais,
  onClose,
}) => {
  if (!isOpen) return null;

  const locale = pais === "Chile" ? "es-CL" : "es-PE";
  // Contratos traen c.cliente directo; PAUTA lo resuelve por facturanumero en oportunidades.
  const clienteDeFactura = (c) =>
    c.cliente || oportunidades.find((o) => o.facturanumero === c.facturanumero)?.cliente || "—";
  const importeCell = (c) =>
    c.moneda_dolar
      ? `US$ ${(c.importeOriginalMoneda || 0).toLocaleString(locale, { maximumFractionDigits: 0 })}`
      : formatCurrency(c.importe);

  const totalAgencia = comisiones.reduce((s, c) => s + c.comision, 0);
  const totalBroker = comisionesBroker.reduce((s, c) => s + c.comisionBroker, 0);
  const totalProgramatico = costosProgramaticos.reduce((s, c) => s + (c.takeLocal || 0), 0);
  const totalApx = apx.reduce((s, c) => s + (c.apxLocal || 0), 0);
  const totalCombinado = totalAgencia + totalBroker + totalProgramatico + totalApx;
  const hayEntries =
    comisiones.length > 0 || comisionesBroker.length > 0 || costosProgramaticos.length > 0 || apx.length > 0;

  // Cada grupo → filas normalizadas a las 6 columnas comunes, con su etiqueta de entidad y de valor.
  const grupos = [
    {
      titulo: "Agencia",
      entidadLabel: "Intermediario",
      valorLabel: "Comisión",
      filas: comisiones.map((c) => ({
        facturanumero: c.facturanumero,
        cliente: clienteDeFactura(c),
        entidad: c.intermediario,
        importe: importeCell(c),
        porcentaje: `${c.porcentaje}%`,
        valor: formatCurrency(c.comision),
      })),
    },
    {
      titulo: "Broker",
      entidadLabel: "Broker",
      valorLabel: "Comisión",
      filas: comisionesBroker.map((c) => ({
        facturanumero: c.facturanumero,
        cliente: clienteDeFactura(c),
        entidad: c.brokerNombre,
        importe: importeCell(c),
        porcentaje: `${c.porcentajeBroker}%`,
        valor: formatCurrency(c.comisionBroker),
      })),
    },
    {
      titulo: "Programática",
      entidadLabel: "Exchange",
      valorLabel: "Take",
      filas: costosProgramaticos.map((c) => ({
        facturanumero: c.facturanumero,
        cliente: clienteDeFactura(c),
        entidad: "Place Exchange",
        importe: importeCell(c),
        porcentaje: c.importe > 0 ? `${((c.takeLocal / c.importe) * 100).toFixed(1)}%` : "—",
        valor: formatCurrency(c.takeLocal),
      })),
    },
    {
      titulo: "APX (Publicis)",
      entidadLabel: "Acuerdo",
      valorLabel: "APX",
      filas: apx.map((c) => ({
        facturanumero: c.facturanumero,
        cliente: clienteDeFactura(c),
        entidad: "APX",
        importe: importeCell(c),
        porcentaje: `${c.porcentaje}%`,
        valor: formatCurrency(c.apxLocal),
      })),
    },
  ];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <h3>Costo intermediación — {mes}</h3>

        {!hayEntries ? (
          <p style={{ margin: "16px 0", color: "#666", fontSize: "0.9rem" }}>
            No hay costo de intermediación en este mes.
          </p>
        ) : (
          grupos
            .filter((g) => g.filas.length > 0)
            .map((g) => (
              <div key={g.titulo} style={{ marginBottom: 18 }}>
                <h4 style={{ margin: "12px 0 6px", fontSize: "0.85rem", color: "#2c3e50" }}>{g.titulo}</h4>
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      <th className={styles.agencyFacturaHeader}>Factura</th>
                      <th className={styles.agencyClienteHeader}>Cliente</th>
                      <th className={styles.agencyIntermediarioHeader}>{g.entidadLabel}</th>
                      <th className={styles.agencyImporteHeader}>Importe</th>
                      <th className={styles.agencyPorcentajeHeader}>%</th>
                      <th className={styles.agencyComisionHeader}>{g.valorLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.filas.map((f, i) => (
                      <tr key={i}>
                        <td className={styles.agencyFacturaData}>{f.facturanumero}</td>
                        <td className={styles.agencyClienteData}>{f.cliente}</td>
                        <td className={styles.agencyIntermediarioData}>{f.entidad}</td>
                        <td className={styles.agencyImporteData}>{f.importe}</td>
                        <td className={styles.agencyPorcentajeData}>{f.porcentaje}</td>
                        <td className={styles.agencyComisionData}>{f.valor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))
        )}

        {hayEntries && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderTop: "2px solid #333",
              paddingTop: 8,
              marginTop: 4,
              fontWeight: 700,
            }}
          >
            <span>Total intermediación</span>
            <span>{formatCurrency(totalCombinado)}</span>
          </div>
        )}

        <div className={styles.modalActions}>
          <button className={styles.btnClose} onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};
